import { Fragment, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { Download, Search } from "lucide-react";

/**
 * Accounting Phase 6 (item 32): this view now shows real accounting data
 * alongside Billing's own outstanding figures, rather than only ever
 * reflecting society_bills. The Accounting-side numbers come from the real
 * Member Receivables control ledger (configured in Billing Ledger Mapping
 * settings) via /society/accounting/engine/ledgers/{id}/party-balances,
 * which derives every balance from actually-posted vouchers - never
 * estimated or copied from Billing. The Billing-side "Bills Outstanding"
 * column is kept as-is (unpaid/partial bills, independent of whether they
 * have posted to Accounting yet) so a real mismatch (e.g. an unmapped head,
 * or a bill still pending posting) is visible rather than hidden.
 */
export default function MemberReceivablesPage() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [accountingLoading, setAccountingLoading] = useState(true);
  // Phase 10 Tally-style redesign: opening balance is entered directly on a
  // member's own row here (no separate Opening Balance Migration screen).
  const [openingRowKey, setOpeningRowKey] = useState("");
  const [openingForm, setOpeningForm] = useState({ amount: "", side: "debit", asOfDate: "" });
  const [savingOpening, setSavingOpening] = useState(false);
  const [search, setSearch] = useState("");
  const [wingFilter, setWingFilter] = useState("all");
  const [selectedKeys, setSelectedKeys] = useState([]);

  const loadAccounting = async () => {
    try {
      const settingsRes = await api.get("/society/accounting/billing-settings").catch(() => ({ data: {} }));
      const s = settingsRes.data || {};
      setSettings(s);
      if (s.memberReceivableLedgerId) {
        const balRes = await api.get(`/society/accounting/engine/ledgers/${s.memberReceivableLedgerId}/party-balances`);
        setLedgerData(balRes.data);
      }
    } catch (err) {
      // Non-fatal - the Billing-only view below still works even if
      // Accounting hasn't been configured for Billing yet.
    } finally {
      setAccountingLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/billing/bills").catch(() => ({ data: [] }));
        setBills(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error(formatErr(err, "Could not load member receivables"));
      } finally {
        setLoading(false);
      }
    })();
    loadAccounting();
  }, []);

  const openOpeningEditor = (row) => {
    setOpeningRowKey(row.key);
    setOpeningForm({ amount: "", side: "debit", asOfDate: "" });
  };

  const saveOpeningBalance = async (row) => {
    if (!settings?.memberReceivableLedgerId) {
      toast.error("Configure the Member Receivables ledger first under Administration -> Billing Ledger Mapping");
      return;
    }
    try {
      setSavingOpening(true);
      await api.put("/society/accounting/engine/members/opening-balance", {
        partyName: row.partyLabel,
        amount: openingForm.amount === "" ? 0 : Number(openingForm.amount),
        side: openingForm.side,
        asOfDate: openingForm.asOfDate,
      });
      toast.success(`Opening balance set for ${row.ownerName || row.partyLabel}`);
      setOpeningRowKey("");
      await loadAccounting();
    } catch (err) {
      toast.error(formatErr(err, "Could not set opening balance"));
    } finally {
      setSavingOpening(false);
    }
  };

  const partyBalanceMap = useMemo(() => {
    const map = new Map();
    for (const p of ledgerData?.parties || []) {
      map.set(p.partyName.trim().toLowerCase(), p);
    }
    return map;
  }, [ledgerData]);

  const ownersOutstanding = useMemo(() => {
    const map = new Map();
    for (const bill of bills) {
      const due = Number(bill.balanceDue || 0);
      if (due <= 0 || bill.status === "cancelled") continue;
      const key = bill.ownerId || `${bill.wing || ""}|${bill.flatNumber || ""}`;
      const entry = map.get(key) || {
        key, wing: bill.wing, flatNumber: bill.flatNumber, ownerName: bill.ownerName, ownerMobile: bill.ownerMobile,
        outstanding: 0, unpaidBills: 0,
      };
      entry.outstanding += due;
      entry.unpaidBills += 1;
      map.set(key, entry);
    }
    return Array.from(map.values())
      .map((row) => {
        // Matches the exact partyName format _post_bill_to_accounting /
        // _post_bill_payment_to_accounting builds in server.py.
        const partyLabel = `${row.ownerName || ""} — Flat ${row.wing || ""}-${row.flatNumber || ""}`;
        const acct = partyBalanceMap.get(partyLabel.trim().toLowerCase());
        return { ...row, partyLabel, accountingBalance: acct ? acct.balance : null, accountingSide: acct ? acct.side : null };
      })
      .sort((a, b) => b.outstanding - a.outstanding);
  }, [bills, partyBalanceMap]);

  const total = ownersOutstanding.reduce((sum, row) => sum + row.outstanding, 0);
  const wings = useMemo(() => Array.from(new Set(ownersOutstanding.map((row) => row.wing).filter(Boolean))).sort(), [ownersOutstanding]);
  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return ownersOutstanding.filter((row) => {
      const matchesWing = wingFilter === "all" || row.wing === wingFilter;
      const haystack = `${row.ownerName || ""} ${row.ownerMobile || ""} ${row.wing || ""} ${row.flatNumber || ""}`.toLowerCase();
      return matchesWing && (!needle || haystack.includes(needle));
    });
  }, [ownersOutstanding, search, wingFilter]);

  const exportSelected = () => {
    const rows = visibleRows.filter((row) => selectedKeys.includes(row.key));
    if (!rows.length) return;
    const csv = [
      ["Flat", "Owner", "Mobile", "Unpaid bills", "Bills outstanding", "Accounting balance", "Side"],
      ...rows.map((row) => [`${row.wing || ""} ${row.flatNumber || ""}`, row.ownerName || "", row.ownerMobile || "", row.unpaidBills, row.outstanding, Math.abs(row.accountingBalance || 0), row.accountingSide || ""]),
    ].map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "member-receivables-selected.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Member Receivables</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Every owner with an unpaid or partially paid bill, sorted by highest outstanding balance. Total outstanding (Billing): ₹ {total.toLocaleString()}.</p>
      </div>

      <SectionCard title="Accounting Control Account">
        {accountingLoading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !settings?.memberReceivableLedgerId ? (
          <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Member Receivables ledger is not yet configured under Administration → Billing Ledger Mapping. Once set, this
            section shows the real double-entry control ledger balance and reconciles it against the sum of member
            subledger balances derived from posted vouchers.
          </div>
        ) : ledgerData ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Control Ledger Balance</div>
              <div className="mt-1 text-xl font-extrabold text-[#111827]">₹ {Math.abs(ledgerData.controlBalance).toLocaleString()} <span className="text-xs font-normal text-[#6B7280]">{ledgerData.controlSide}</span></div>
              <div className="text-xs text-[#6B7280]">{ledgerData.ledgerName}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Sum of Member Subledgers</div>
              <div className="mt-1 text-xl font-extrabold text-[#111827]">₹ {Math.abs(ledgerData.subledgerTotal).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Reconciliation</div>
              <div className={`mt-1 text-xl font-extrabold ${ledgerData.reconciled ? "text-emerald-600" : "text-red-600"}`}>{ledgerData.reconciled ? "Matched" : "Mismatch"}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#6B7280]">No posted accounting activity yet for the Member Receivables ledger.</div>
        )}
      </SectionCard>

      <div className="flex flex-col gap-3 rounded-sm border border-[#E5E7EB] bg-white p-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-[#6B7280]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Search owner, flat or mobile" />
        </div>
        <Select value={wingFilter} onValueChange={setWingFilter}>
          <SelectTrigger className="w-full md:w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All wings</SelectItem>{wings.map((wing) => <SelectItem key={wing} value={wing}>{wing}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" disabled={!selectedKeys.length} onClick={exportSelected}><Download className="mr-2 h-4 w-4" />Export selected ({selectedKeys.length})</Button>
      </div>

      <div className="overflow-x-auto rounded-sm border border-[#E5E7EB] bg-white">
        <Table>
          <TableHeader>
            <TableRow><TableHead className="w-10"><input type="checkbox" aria-label="Select all visible receivables" checked={visibleRows.length > 0 && visibleRows.every((row) => selectedKeys.includes(row.key))} onChange={(e) => setSelectedKeys(e.target.checked ? Array.from(new Set([...selectedKeys, ...visibleRows.map((row) => row.key)])) : selectedKeys.filter((key) => !visibleRows.some((row) => row.key === key)))} /></TableHead><TableHead>Flat</TableHead><TableHead>Owner</TableHead><TableHead>Mobile</TableHead><TableHead>Unpaid bills</TableHead><TableHead className="text-right">Bills Outstanding</TableHead><TableHead className="text-right">Accounting Balance</TableHead><TableHead className="text-right">Opening Balance</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
            ) : visibleRows.map((row) => (
              <Fragment key={row.key}>
                <TableRow>
                  <TableCell><input type="checkbox" aria-label={`Select ${row.ownerName || row.key}`} checked={selectedKeys.includes(row.key)} onChange={(e) => setSelectedKeys((keys) => e.target.checked ? Array.from(new Set([...keys, row.key])) : keys.filter((key) => key !== row.key))} /></TableCell>
                  <TableCell>{row.wing} {row.flatNumber}</TableCell>
                  <TableCell>{row.ownerName || "-"}</TableCell>
                  <TableCell>{row.ownerMobile || "-"}</TableCell>
                  <TableCell>{row.unpaidBills}</TableCell>
                  <TableCell className="text-right font-semibold">₹ {row.outstanding.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {row.accountingBalance === null ? <span className="text-xs text-[#9CA3AF]">Not posted</span> : `₹ ${Math.abs(row.accountingBalance).toLocaleString()} ${row.accountingSide}`}
                  </TableCell>
                  <TableCell className="text-right">
                    <button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => (openingRowKey === row.key ? setOpeningRowKey("") : openOpeningEditor(row))}>
                      {openingRowKey === row.key ? "Cancel" : "Set opening balance"}
                    </button>
                  </TableCell>
                </TableRow>
                {openingRowKey === row.key && (
                  <TableRow key={`${row.key}-opening`}>
                    <TableCell colSpan={8} className="bg-[#F9FAFB]">
                      <div className="flex flex-wrap items-end gap-3 py-1">
                        <div className="w-32">
                          <label className="mb-1 block text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">Amount</label>
                          <Input type="number" min="0" step="0.01" value={openingForm.amount} onChange={(e) => setOpeningForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                        </div>
                        <div className="w-44">
                          <label className="mb-1 block text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">Balance is</label>
                          <Select value={openingForm.side} onValueChange={(v) => setOpeningForm((f) => ({ ...f, side: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="debit">Dr (member owes - typical)</SelectItem>
                              <SelectItem value="credit">Cr (member is in advance)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-40">
                          <label className="mb-1 block text-[11px] uppercase tracking-[0.12em] text-[#6B7280]">As of date</label>
                          <Input type="date" value={openingForm.asOfDate} onChange={(e) => setOpeningForm((f) => ({ ...f, asOfDate: e.target.value }))} />
                        </div>
                        <Button disabled={savingOpening} onClick={() => saveOpeningBalance(row)} className="bg-[#111827] text-white hover:bg-[#374151]">Save</Button>
                      </div>
                      <p className="pb-2 text-xs text-[#6B7280]">
                        What {row.ownerName || "this member"} already owed (or had as advance) as of the date above. Saved immediately - no separate posting step. Blocked once this member has any posted bill or payment.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {!loading && visibleRows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-[#6B7280]">No matching outstanding balances.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-[#6B7280]">To collect a payment or resend a bill, use Society Panel &rarr; Billing &rarr; Outstanding.</p>
    </div>
  );
}
